import React from 'react';
import clsx from 'clsx';
import styles from '../WarrantyRequestModal.module.scss';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { requiredRule } from '../../../../helpers/form/react-hook-form-helper.ts';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import { AdvancedFormReturnType } from '../../../../helpers/form/useAdvancedForm.ts';
import {
  Form,
  WarrantyRequestModalCategory,
} from '../WarrantyRequestModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { UseQueryResult } from '@tanstack/react-query';
import {
  ProblemDetails,
  WarrantyResponseDto,
} from '../../../../services/api/api-client.types.ts';
import { useAnalytics } from 'helpers/analytics.ts';
import { removeExtraLetters } from '../warranty-helper.ts';

type OwnProps = {
  form: AdvancedFormReturnType<Form>;
  device: UseQueryResult<WarrantyResponseDto, ProblemDetails>;
  turnOffSerialNumberEditing: () => void;
  serialNumber: string;
};

export const SerialNumberEditing = (props: OwnProps) => {
  const { form, device, turnOffSerialNumberEditing, serialNumber } = props;
  const { trackEvent } = useAnalytics();

  const i18n = useScopedTranslation('Page.WarrantyRequest');

  async function searchSerialNumber() {
    trackEvent({
      category: WarrantyRequestModalCategory,
      action: 'Search serial number',
      name: `Serial number: ${form.getValues('serialNumber')}`,
    });

    const formattedSerialNumber = serialNumber.replace(/\.+$/, '');
    form.setValue('serialNumber', formattedSerialNumber);
    const result = await form.trigger('serialNumber');
    if (result) {
      turnOffSerialNumberEditing();
    }
  }

  return (
    <div
      className={clsx(
        styles.rowContainer,
        styles.columnGap10,
        styles.justifyContentSpaceBetween,
        styles.marginTop33,
      )}
    >
      <Input
        testId={'sn-input'}
        {...form.register(
          'serialNumber',
          requiredRule(i18n.t('required_error')),
        )}
        onChange={(e) =>
          form.setValue(
            'serialNumber',
            removeExtraLetters(e.target.value) ?? '',
          )
        }
        errorText={
          form.formState.errors.serialNumber?.message ||
          (device.isError
            ? device.error?.response.detail ?? undefined
            : undefined)
        }
        topRightHelperText={i18n.t('required')}
        topHelperText={i18n.t('serial_number')}
        fullWidth={true}
        onEnterPressed={async () => {
          await searchSerialNumber();
        }}
        maxLength={15}
      />
      <ActionText
        testId={'sn-search'}
        className={
          !form.formState.errors.serialNumber?.message &&
          !device.isError &&
          styles.negativeMarginBottom
        }
        text={i18n.t('search')}
        onClickHandler={async () => {
          await searchSerialNumber();
        }}
      />
      <Loading
        loading={device.isLoading}
        wrapperClassName={styles.loader}
        className={styles.absoluteContainer}
      />
    </div>
  );
};
